import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import logger from "../utils/logger";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

/**
 * A wrapper component specifically designed to be rendered inside the detached portal window.
 * It injects an isolated `ToastContainer` so that any toast notifications triggered
 * by components inside the new window render correctly within that window, 
 * rather than attempting to render in the parent application window.
 * 
 * @param {Object} props - The component properties.
 * @param {React.ReactNode} props.children - The child components to render.
 * @returns {JSX.Element} The rendered content wrapped with toast context.
 */
const PortalRoot = ({ children }) => {
  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{ zIndex: 999999 }}
      />
      {children}
    </>
  );
};
PortalRoot.propTypes = {
  children: PropTypes.node
};

PortalRoot.defaultProps = {
  children: null
};
/**
 * Creates a new browser popup window and uses React Portals to render React components inside of it.
 * This component handles the complexities of popup blockers, CSS stylesheet cloning,
 * and safely monitoring for cross-origin redirects (like IAP auth flows) that might sever window access.
 * 
 * @param {Object} props - The component properties.
 * @param {React.ReactNode} props.children - The React content to render in the popup window.
 * @param {Function} props.onClose - Callback function triggered when the popup is closed, blocked, or lost.
 * @returns {React.ReactPortal|null} The React portal mounting the children, or null if the container isn't ready.
 */
const NewWindowPortal = ({ children, onClose }) => {
  const [containerEl, setContainerEl] = useState(null);
  const windowRef = useRef(null);


  /**
* Effect hook to manage the full lifecycle of the detached popup window.
* Runs exactly once when the component mounts.
* 
* Responsibilities:
* 1. Open the physical popup window.
* 2. Safely create a mount point (`div`) inside the new window's document.
* 3. Clone all CSS stylesheets from the parent to ensure styling is identical.
* 4. Inject custom CSS specifically for Toasts.
* 5. Setup an interval to monitor if the user closes the window, or if an auth redirect breaks CORS access.
*/
  useEffect(() => {
    // 1. Open the window
    const newWindow = window.open(
      '',
      'PromptLibrary',
      'width=1000,height=800,resizable=yes,scrollbars=yes'
    );

    windowRef.current = newWindow;

    if (!newWindow) {
      alert("Popup blocked! Please allow popups.");
      onClose();
      return;
    }

    // Wrap DOM access in try-catch
    try {
      // Create the div INSIDE the new window 
      const el = newWindow.document.createElement('div');
      newWindow.document.body.appendChild(el);
      newWindow.document.title = "Prompt Library";

      // Copy Styles safely
      const styleSheets = Array.from(document.styleSheets);
      styleSheets.forEach(styleSheet => {
        try {
          if (styleSheet.href) {
            const link = newWindow.document.createElement('link');
            link.rel = 'stylesheet';
            link.href = styleSheet.href;
            newWindow.document.head.appendChild(link);
          } else if (styleSheet.cssRules) {
            const cssRules = Array.from(styleSheet.cssRules).map(rule => rule.cssText).join('');
            const style = newWindow.document.createElement('style');
            style.appendChild(newWindow.document.createTextNode(cssRules));
            newWindow.document.head.appendChild(style);
          }
        } catch  {
          // Ignore cross-origin stylesheet errors
        }
      });

      const customToastStyles = `
        /* Font matching Word Add-in / Fluent UI */
        .Toastify__toast-body {
          font-family: "Segoe UI", "Segoe UI Web (West European)", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif;
          font-size: 14px;
          font-weight: 600;
        }

        /* SUCCESS: Match App.js (#dff6dd background, #107c10 text) */
        .Toastify__toast--success {
          background: #dff6dd !important;
          color: #107c10 !important;
          border: none !important;
        }
        /* Change the default green icon to your dark green */
        .Toastify__toast--success svg {
          fill: #107c10 !important;
        }

        /* ERROR: Match App.js (#fde7e9 background, #d13438 text) */
        .Toastify__toast--error {
          background: #fde7e9 !important;
          color: #d13438 !important;
          border:none !important;
        }
        /* Change the default red icon to your dark red */
        .Toastify__toast--error svg {
          fill: #d13438 !important;
        }

        /* Close Button Color (inherit from text color) */
        .Toastify__close-button {
          color: inherit !important;
          opacity: 0.7;
        }
      `;

      const styleEl = newWindow.document.createElement('style');
      styleEl.appendChild(newWindow.document.createTextNode(customToastStyles));
      newWindow.document.head.appendChild(styleEl);
      // 4. Set the container state to start React rendering
      setContainerEl(el);

    } catch (error) {
      console.error("Failed to setup popup window (likely blocked or redirected):", error);
      newWindow.close();
      onClose();
      return;
    }

    // Logic for window close handlers...
    const handleParentUnload = () => {
      if (newWindow && !newWindow.closed) {
        newWindow.close();
      }
    };
    window.addEventListener('beforeunload', handleParentUnload);
    // Watch for Closure or Redirects
    const interval = setInterval(() => {
      try {
        // If window is closed OR if we lost access to the document (Redirect happened)
        if (!newWindow || newWindow.closed) {
          onClose();
          clearInterval(interval);
        } else {
          // Verify we can still touch the document. If this throws, we lost access.
          // eslint-disable-next-line no-unused-vars    
          const checkAccess = newWindow.document.title;
        }
      } catch {
        // We lost access (IAP Redirected the window). 
        // Stop React from trying to render immediately.
        logger.warn('Lost access to Prompt Library window (likely IAP redirect). Closing portal.');
        setContainerEl(null); // Unmounts the portal content immediately
        onClose();
        clearInterval(interval);
      }
    }, 500);

    // Cleanup on Unmount
    return () => {
      clearInterval(interval);
      if (newWindow && !newWindow.closed) {
        newWindow.close();
      }
    };
  }, []); // Run once on mount

  // If we don't have a valid container, render NULL.
  // This stops React from trying to write to a blocked window.
  if (!containerEl) return null;

  return ReactDOM.createPortal(
    <PortalRoot>{children}</PortalRoot>,
    containerEl
  );
};
NewWindowPortal.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired
};

NewWindowPortal.defaultProps = {
  children: null
};
export default NewWindowPortal;